import { useEffect, useState, useMemo, useCallback } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import type { Product } from '@/types/database';
import {
  Package,
  Wrench,
  Plus,
  Loader2,
  Search,
  ChevronDown,
  Image,
  Clock,
  ScanLine,
  FileSpreadsheet,
  Boxes,
  AlertTriangle,
  Layers,
  Coins,
  Percent,
  ArrowUpDown,
  SlidersHorizontal,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { StorageImage } from '@/components/ui/StorageImage';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useXlsxExport } from '@/hooks/useXlsxExport';
import { buildCatalogXlsx } from '@/lib/xlsxTemplates';
import { BulkActionBar } from '@/components/bulk-actions/BulkActionBar';
import { BulkDeleteDialog } from '@/components/bulk-actions/BulkDeleteDialog';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { cacheCatalog, getCachedCatalog } from '@/lib/offlineDataCache';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useRole } from '@/hooks/useRole';
import { addToOfflineQueue, removeFromOfflineQueue } from '@/lib/offlineStorage';
import { isTrackedProduct, isLowStock, formatStockQuantity } from '@/lib/stock';
import {
  groupVariants,
  isVariant,
  priceRange,
  totalVariantStock,
  hasTrackedVariant,
  variantDisplayName,
  withVariantIds,
  type VariantGroup,
} from '@/lib/variants';
import { deleteProductsWithVariants } from '@/lib/productMutations';
import { unitLabel as centralUnitLabel } from '@/lib/units';
import { BulkCostModal } from '@/components/catalog/BulkCostModal';
import { BulkPriceAdjustModal } from '@/components/catalog/BulkPriceAdjustModal';
import { type BundleItemRow } from '@/lib/productBundles';
import { cacheBundles, getCachedBundles } from '@/lib/offlineDataCache';
import { cn } from '@/lib/utils';
import { CONTENT_CARD } from '@/components/ui/field';
import { plural } from '@/lib/plural';
import {
  FILTER_CHIP_COMPACT,
  FILTER_CHIP_OFF,
  FILTER_CHIP_ON,
  FILTER_CHIP_ROW,
} from '@/components/filters/filterChips';
import { ListFiltersDialog, type FilterGroup } from '@/components/filters/ListFiltersDialog';
import { EMPTY_LIST_RANGE, type ListValueRange } from '@/lib/listFilters';
import {
  CATALOG_SORT_LABELS,
  CATALOG_SORT_ORDER,
  CATALOG_STOCK_FILTERS,
  DEFAULT_CATALOG_SORT,
  catalogGroupMatchesSearch,
  catalogListTotalLabel,
  countActiveCatalogFilters,
  matchesPriceRange,
  matchesStockFilter,
  sortCatalogGroups,
  summarizeCatalogList,
  type CatalogSortOption,
  type CatalogStockFilter,
} from '@/lib/catalogListFilters';

type ItemType = 'service' | 'product';

interface CatalogItem extends Product {
  type: ItemType;
  unit: string | null;
  category: string | null;
}

// As unidades vivem em `src/lib/units.ts` — este arquivo tinha a sua própria
// lista de 6 códigos que NÃO existiam no orçamento (`hour` ≠ `h`,
// `service` ≠ `srv`…), então um serviço cadastrado aqui aparecia sem unidade lá.
// Ver o cabeçalho de units.ts.

/** Chave do grupo de estoque no modal de filtros. */
const STOCK_GROUP_KEY = 'stock';

export default function CatalogPage() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const navigate = useNavigate();
  const { isOnline, checkOnline } = useOfflineSync();
  // Team roles: members+ create/edit; only owner/admin delete.
  const { canWrite, canDelete } = useRole();


  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortOption, setSortOption] = useState<CatalogSortOption>(DEFAULT_CATALOG_SORT);
  const [stockFilter, setStockFilter] = useState<CatalogStockFilter>('all');
  const [priceFilter, setPriceFilter] = useState<ListValueRange>(EMPTY_LIST_RANGE);
  const [filtersOpen, setFiltersOpen] = useState(false);
  /** MODO DE SELEÇÃO — desligado por padrão (#192). */
  const [selectionMode, setSelectionMode] = useState(false);
  // `?tab=product` abre direto na aba de produtos — usado pelos contadores de
  // cadastro da home, que apontam para o balde que o usuário tocou.
  const [activeTab, setActiveTab] = useState<ItemType>(
    () => (new URLSearchParams(window.location.search).get('tab') === 'product' ? 'product' : 'service'),
  );
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDuplicating, setBulkDuplicating] = useState(false);
  const [bulkCostOpen, setBulkCostOpen] = useState(false);
  const [bulkPriceAdjustOpen, setBulkPriceAdjustOpen] = useState(false);
  // Cards de pai expandidos na lista (mostram as filhas aninhadas).
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  /**
   * Materiais inclusos (kits) da empresa inteira, linha a linha. A lista toda
   * fica em memória porque a tela precisa saber, para CADA card, se ele é um kit
   * e se ele já é material de outro (regra de nível único).
   */
  const [bundleRows, setBundleRows] = useState<BundleItemRow[]>([]);

  const { scan: scanBarcode, isAvailable: barcodeAvailable, isScanning: isBarcodeScanning } = useBarcodeScanner();
  const showBarcodeButton = barcodeAvailable || import.meta.env.DEV;

  // Scanner da barra de busca: procura um produto pelo código de barras e,
  // se não achar, abre o cadastro de produto novo com o código já preenchido.
  const handleScanBarcode = useCallback(async () => {
    const code = await scanBarcode();
    if (!code) return;

    const scanned = code.trim();
    // A busca varre a lista PLANA, então acha tanto produtos simples quanto
    // VARIAÇÕES (cada variação tem código de barras próprio).
    const match = items.find(
      (item) =>
        (item.barcode && item.barcode === scanned) ||
        // Fallback legado: produtos antigos guardavam o código na descrição.
        (item.description && item.description.includes(scanned)),
    );
    if (match) {
      setActiveTab(match.type);

      if (isVariant(match)) {
        // Achamos a variação: navega pelo PAI (é ele que aparece na lista) e
        // abre o card para a variação encontrada ficar visível.
        const parent = items.find((i) => i.id === match.parent_product_id);
        setSearch(parent?.name ?? match.name);
        if (parent) setExpandedParents((prev) => new Set(prev).add(String(parent.id)));
        toast.success(`Encontrado: ${variantDisplayName(parent?.name ?? match.name, match.variant_name)}`);
        return;
      }

      setSearch(match.name);
      toast.success(`Encontrado: ${match.name}`);
      return;
    }

    setActiveTab('product');
    navigate('/catalog/new', { state: { initialType: 'product', initialBarcode: scanned } });
    toast.info('Produto novo — código de barras preenchido.');
  }, [scanBarcode, items, navigate]);

  // Categorias distintas do tab atual — alimentam a faixa de recorte.
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .filter((i) => i.type === activeTab && i.category?.trim())
            .map((i) => i.category!.trim()),
        ),
      ).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [items, activeTab],
  );

  // As variações por pai, a partir da lista PLANA que vem do banco. A consulta
  // de `products` traz pais e filhas misturados (custo único da arquitetura de
  // variação-é-produto) — daqui para baixo a tela só enxerga a hierarquia.
  const variantsByParent = useMemo(() => groupVariants(items), [items]);

  /** Só as RAÍZES do tab ativo, cada uma com as filhas anexadas. */
  const tabGroups: VariantGroup<CatalogItem>[] = useMemo(
    () =>
      items
        .filter((item) => !isVariant(item) && item.type === activeTab)
        .map((parent) => {
          const variants = (variantsByParent.get(String(parent.id)) ?? []) as CatalogItem[];
          return { parent, variants, hasVariants: variants.length > 0 };
        }),
    [items, activeTab, variantsByParent],
  );

  /**
   * O recorte SEM a categoria — é ele que alimenta as contagens dos botões.
   *
   * Contar sobre a lista inteira faria o botão de uma categoria prometer 12
   * com uma busca ativa que devolve 3; contar depois da categoria zeraria
   * todos os outros botões (#193).
   */
  const groupsBeforeCategory = useMemo(
    () =>
      tabGroups.filter(
        (group) =>
          catalogGroupMatchesSearch(group, search) &&
          matchesStockFilter(group, stockFilter) &&
          matchesPriceRange(group, priceFilter),
      ),
    [tabGroups, search, stockFilter, priceFilter],
  );

  /** Quantos itens cada botão de categoria traria. */
  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const group of groupsBeforeCategory) {
      const key = (group.parent.category ?? '').trim();
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [groupsBeforeCategory]);

  /** Raízes + filhas anexadas — o que a lista renderiza, já ordenado. */
  const visibleGroups = useMemo(
    () =>
      sortCatalogGroups(
        groupsBeforeCategory.filter(
          (group) =>
            categoryFilter === 'all' || (group.parent.category ?? '').trim() === categoryFilter,
        ),
        sortOption,
      ) as VariantGroup<CatalogItem>[],
    [groupsBeforeCategory, categoryFilter, sortOption],
  );

  const filteredItems = useMemo(() => visibleGroups.map((g) => g.parent), [visibleGroups]);

  const activeModalFilters = countActiveCatalogFilters(stockFilter, priceFilter);
  const recorteAtivo =
    search.trim().length > 0 ||
    categoryFilter !== 'all' ||
    stockFilter !== 'all' ||
    activeModalFilters > 0;
  const summary = summarizeCatalogList(visibleGroups);

  // ===== Materiais inclusos (kits) — derivações da lista de ligações =====
  /** Quantos serviços incluem cada produto — alimenta o aviso da exclusão. */
  const kitUsageByChild = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of bundleRows) {
      const key = String(row.child_product_id);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [bundleRows]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const {
    selectedCount,
    allSelected,
    isSelected,
    toggleItem,
    clearSelection,
    toggleSelectAll,
    selectedIds,
  } = useBulkSelection(filteredItems);

  const { exportXlsx } = useXlsxExport();

  // Export the current selection (active tab) or the entire catalog. The
  // builder splits items into "Serviços" and "Produtos" sheets by type.
  const handleExportXlsx = () => {
    // A seleção é feita sobre os cards (raízes), mas a planilha lista as
    // VARIAÇÕES como linhas próprias — então expandimos a seleção para incluir
    // as filhas, senão exportar uma "Camiseta" selecionada sairia sem P/M/G.
    const source =
      selectedCount > 0
        ? (() => {
            const ids = new Set(withVariantIds(Array.from(selectedIds), items));
            return items.filter((i) => ids.has(String(i.id)));
          })()
        : items;

    exportXlsx(() => buildCatalogXlsx(source), 'catalogo');
  };

  // Get effective user ID from user context or cached session
  const getEffectiveUserId = useCallback((): string | null => {
    if (user?.id) return user.id;

    try {
      const cachedSession = localStorage.getItem('orcamigo_cache_auth_session');
      if (cachedSession) {
        const sessionData = JSON.parse(cachedSession);
        if (sessionData?.data?.userId) {
          return sessionData.data.userId;
        }
      }
    } catch (e) {
      console.error('[CatalogPage] Error reading cached session:', e);
    }
    return null;
  }, [user]);

  // Load cached data immediately on mount (critical for offline app restart)
  // This runs synchronously on mount to show cached data instantly
  useEffect(() => {
    const userId = getEffectiveUserId();

    if (userId) {
      const cached = getCachedCatalog(userId);
      if (cached !== null && cached.length > 0) {
        const mappedCached: CatalogItem[] = cached.map((item: any) => ({
          ...item,
          type: item.type || 'service',
          unit: item.unit || null,
        }));
        setItems(mappedCached);
        setLoading(false);
      } else if (!isOnline) {
        // No cache and offline - stop loading
        setLoading(false);
      }
    } else if (!isOnline) {
      // No userId and offline - stop loading
      setLoading(false);
    }
  }, [getEffectiveUserId, isOnline]);

  // Fetch from server when online - always try to fetch for latest data
  useEffect(() => {
    const userId = getEffectiveUserId();
    if (!userId) return;
    if (!isOnline) return;
    // Only fetch if user context is available (required for supabase query)
    if (!user) return;
    if (!activeCompany) return;

    fetchItems();
  }, [user, isOnline, getEffectiveUserId, activeCompany?.id]);

  // Listen for sync completion to refresh data
  useEffect(() => {
    if (!user) return;

    const handleCatalogUpdated = () => {
      if (isOnline) {
        fetchItems();
      } else {
        // Reload from cache
        const cached = getCachedCatalog(user.id);
        if (cached) {
          const mappedCached: CatalogItem[] = cached.map((item: any) => ({
            ...item,
            type: item.type || 'service',
            unit: item.unit || null,
          }));
          setItems(mappedCached);
        }
      }
    };
    window.addEventListener('catalog-updated', handleCatalogUpdated);

    return () => {
      window.removeEventListener('catalog-updated', handleCatalogUpdated);
    };
  }, [user, isOnline]);

  // Clear selection when the RECORTE changes. A ORDENAÇÃO fica de fora: ela
  // reorganiza as mesmas linhas, então a seleção continua válida.
  useEffect(() => {
    clearSelection();
  }, [search, activeTab, categoryFilter, stockFilter, priceFilter, clearSelection]);

  // Os recortes são por-aba: zeram ao trocar entre serviços/produtos. A
  // categoria porque o vocabulário é outro; o ESTOQUE porque serviço não tem
  // saldo, e um "Sem saldo" herdado da aba de produtos esvaziaria a lista de
  // serviços sem nenhum controle visível explicando por quê.
  useEffect(() => {
    setCategoryFilter('all');
    setStockFilter('all');
  }, [activeTab]);

  const fetchItems = async () => {
    if (!user || !activeCompany) return;

    try {
      const { data, error } = await supabase
        .from('products')
        .select('*, product_photos(*)')
        .eq('company_id', activeCompany.id)
        .order('name');

      if (data && !error) {
        // Merge server data with offline-pending items from cache
        const existingCache = (getCachedCatalog(user.id) || []) as any[];
        const offlinePending = existingCache.filter(
          (p) => p._offline_pending === true && String(p.id).startsWith('offline_')
        );

        // Create a set of server IDs for fast lookup
        const serverIds = new Set(data.map((p: any) => p.id));

        // Keep offline-pending items that haven't synced yet
        const merged = [
          ...data,
          ...offlinePending.filter((p) => !serverIds.has(p.id)),
        ];

        const mappedItems: CatalogItem[] = merged.map((item: any) => ({
          ...item,
          type: item.type || 'service',
          unit: item.unit || null,
        }));
        setItems(mappedItems);
        // Cache merged list for offline access
        cacheCatalog(user.id, merged);
      } else {
        // Fallback to cache if database fetch fails (e.g. offline/network error)
        const cached = getCachedCatalog(user.id);
        if (cached) {
          const mappedCached: CatalogItem[] = cached.map((item: any) => ({
            ...item,
            type: item.type || 'service',
            unit: item.unit || null,
          }));
          setItems(mappedCached);
        }
      }
    } catch (e) {
      console.error('[CatalogPage] Error fetching items:', e);
      // On error, keep cached data if available
    }
    setLoading(false);
  };

  /**
   * Carrega os vínculos de kit da empresa. Tenta o servidor e cai no cache —
   * a leitura precisa funcionar offline (o orçamento monta o kit sem rede),
   * mesmo que criar/remover vínculo seja online-only.
   */
  const fetchBundles = useCallback(async () => {
    const userId = getEffectiveUserId();
    if (!userId) return;

    const cached = getCachedBundles(userId);
    if (cached) setBundleRows(cached as BundleItemRow[]);

    if (!isOnline || !activeCompany) return;
    const { data, error } = await supabase
      .from('product_bundle_items')
      .select('*')
      .eq('company_id', activeCompany.id);

    if (!error && data) {
      setBundleRows(data as BundleItemRow[]);
      cacheBundles(userId, data);
    }
  }, [getEffectiveUserId, isOnline, activeCompany?.id]);

  useEffect(() => {
    fetchBundles();
  }, [fetchBundles]);

  const handleBulkDelete = async () => {
    if (!canDelete) {
      toast.warning('Apenas o proprietário e administradores podem excluir.');
      return;
    }
    setBulkDeleting(true);

    const idsToDelete = Array.from(selectedIds);

    const online = await checkOnline();
    if (!online && user) {
      const cached = (getCachedCatalog(user.id) || []) as any[];
      let next = cached;

      for (const itemId of idsToDelete) {
        if (String(itemId).startsWith('offline_')) {
          removeFromOfflineQueue(itemId);
        } else {
          addToOfflineQueue('delete_catalog_item', { itemId, userId: user.id });
        }
        // Remove também as variações deste pai do cache local.
        const idsToRemove = new Set(withVariantIds([String(itemId)], items));
        next = next.filter((p) => !idsToRemove.has(String(p.id)));
      }

      cacheCatalog(user.id, next);
      const mappedNext: CatalogItem[] = next.map((item: any) => ({
        ...item,
        type: item.type || 'service',
        unit: item.unit || null,
      }));
      setItems(mappedNext);
      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('catalog-updated'));

      setBulkDeleting(false);
      toast.success(`${idsToDelete.length} item(ns) excluído(s) localmente!`, { icon: '📴' });
      setBulkDeleteOpen(false);
      clearSelection();
      return;
    }

    // Mesmo cuidado do delete individual: o helper busca as variações no banco
    // e desvincula os `quote_items` de pais e filhas antes de apagar.
    const { error } = await deleteProductsWithVariants(idsToDelete);

    setBulkDeleting(false);

    if (error) {
      toast.error('Erro ao excluir itens do catálogo');
      return;
    }

    toast.success(`${idsToDelete.length} item(ns) excluído(s)!`);
    setBulkDeleteOpen(false);
    clearSelection();
    fetchItems();
    fetchBundles();
  };

  /**
   * Abre o ajuste de custo em massa. É gravação direta no servidor (como o
   * reajuste de preço e a duplicação em massa), então exige conexão — a fila
   * offline enfileira UMA operação por item e não tem executor de update em
   * lote de catálogo.
   */
  const handleOpenBulkCost = async () => {
    if (!canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }
    const online = await checkOnline();
    if (!online) {
      toast.warning('Definir custo em massa exige conexão com a internet.');
      return;
    }
    setBulkCostOpen(true);
  };

  const handleOpenBulkPriceAdjust = async () => {
    if (!canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }
    const online = await checkOnline();
    if (!online) {
      toast.warning('Reajustar preços em massa exige conexão com a internet.');
      return;
    }
    setBulkPriceAdjustOpen(true);
  };

  const handleBulkDuplicate = async () => {
    setBulkDuplicating(true);

    const itemsToDuplicate = filteredItems.filter(item => selectedIds.has(item.id));

    try {
      for (const item of itemsToDuplicate) {
        const copyName = `${item.name} (Cópia)`;
        const { data: createdCopy } = await supabase
          .from('products')
          .insert({
            user_id: user!.id,
            company_id: activeCompany?.id ?? item.company_id ?? null,
            name: copyName,
            description: item.description,
            price: item.price,
            cost: item.cost ?? null,
            type: item.type,
            unit: item.unit,
            category: item.category,
            // SKU novo não herda código de barras nem código interno
            // (ver CatalogItemFormPage.handleDuplicate).
            barcode: null,
            internal_code: null,
            lc116_item: item.lc116_item,
            iss_aliquota: item.iss_aliquota,
            iss_retido: item.iss_retido === true,
            nbs: item.nbs,
            cclasstrib: item.cclasstrib,
            // Copia rastreio + mínimo; saldo começa em 0 (SKU novo).
            track_stock: item.track_stock,
            min_stock: item.min_stock,
          })
          .select('id');

        // A cópia leva junto as variações (senão nasce um pai vazio).
        const copyId = createdCopy?.[0] ? String(createdCopy[0].id) : null;
        if (copyId) {
          const children = (variantsByParent.get(String(item.id)) ?? []) as CatalogItem[];
          for (const child of children) {
            await supabase.from('products').insert({
              user_id: user!.id,
              company_id: activeCompany?.id ?? child.company_id ?? null,
              parent_product_id: copyId,
              variant_name: child.variant_name,
              name: copyName,
              description: child.description,
              price: child.price,
              cost: child.cost ?? null,
              type: 'product',
              unit: child.unit,
              category: child.category,
              barcode: null,
              internal_code: null,
              track_stock: child.track_stock,
              min_stock: child.min_stock,
            });
          }
        }
      }

      toast.success(`${itemsToDuplicate.length} item(ns) duplicado(s)!`);
      clearSelection();
      fetchItems();
    } catch (error) {
      console.error('Error in bulk duplicate:', error);
      toast.error('Erro ao duplicar itens');
    } finally {
      setBulkDuplicating(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getUnitLabel = (unitValue: string | null, type: ItemType) => centralUnitLabel(unitValue, type) || null;

  /**
   * Grupos do modal. O ESTOQUE só existe na aba de produtos: serviço não tem
   * saldo, e oferecer "Sem saldo" ali seria um interruptor que não faz nada —
   * pior do que interruptor nenhum (#91).
   */
  const filterGroups: FilterGroup[] =
    activeTab === 'product'
      ? [
          {
            key: STOCK_GROUP_KEY,
            label: 'Estoque',
            options: CATALOG_STOCK_FILTERS.map((o) => ({ value: o.value, label: o.label })),
          },
        ]
      : [];

  if (!user) {
    return (
      <MainLayout showFab={false}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Package className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Catálogo</h2>
          <p className="mb-4 text-muted-foreground">
            Faça login para gerenciar seu catálogo
          </p>
          <Button3D onClick={() => navigate('/auth')}>Fazer login</Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideFab={selectedCount > 0}>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app (o mesmo de /quotes e /clients)     */}
        {/* ------------------------------------------------------------------ */}
        {/* Nona tela no molde. O que precisou ser traduzido:

            · O H1 acompanha a ABA (Serviços × Produtos), como em /quotes ele
              acompanha Orçamentos × Vendas: é o hero afirmando de qual conjunto
              o número logo abaixo saiu. Um cabeçalho fixo em "Catálogo" com uma
              contagem de produtos embaixo seria a mentira do #182/#188 uma
              linha acima do número.
            · O VALOR AO VIVO é a CONTAGEM do recorte — e ela conta só RAÍZES:
              uma camiseta P/M/G é UM item de catálogo, não quatro (#30). Por
              isso a sublinha diz quantas variações estão penduradas nesses
              itens, senão o número pareceria menor do que o catálogo é.
            · Estoque baixo entra na sublinha porque é a única informação
              ACIONÁVEL de uma lista de cadastro — e é o que dá sentido ao
              filtro "Estoque baixo" do modal.
            · Sem botão de voltar: `/catalog` é destino da navegação inferior. */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Catálogo</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">
                {activeTab === 'service' ? 'Serviços' : 'Produtos'}
              </h1>
            </div>

            {/* Ações SECUNDÁRIAS viram ícone (#186). Com "Exportar" e
                "Reajustar" em texto, os três botões somavam mais que a linha e
                o excedente saía da tela — levando junto o "Novo", que é o
                último filho e a ação que menos pode sumir. `aria-label`/`title`
                são obrigatórios aqui, senão o botão perde o nome acessível
                junto com o texto (#142). */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleExportXlsx}
              disabled={items.length === 0}
              className="relative h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white disabled:opacity-40"
              aria-label={
                selectedCount > 0
                  ? `Exportar ${plural(selectedCount, 'selecionado', 'selecionados')}`
                  : 'Exportar'
              }
              title={selectedCount > 0 ? `Exportar (${selectedCount})` : 'Exportar catálogo'}
            >
              <FileSpreadsheet className="h-4 w-4" />
              {selectedCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold leading-none text-emerald-800">
                  {selectedCount}
                </span>
              )}
            </Button>

            {canWrite && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleOpenBulkPriceAdjust}
                disabled={items.length === 0}
                className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white disabled:opacity-40"
                aria-label="Reajustar preços em massa"
                title="Reajustar preços em massa"
              >
                <Percent className="h-4 w-4" />
              </Button>
            )}

            {canWrite && (
              /* A ação PRIMÁRIA mantém o texto. */
              <Button3D
                size="sm"
                color="light"
                className="shrink-0"
                onClick={() => navigate('/catalog/new', { state: { initialType: activeTab } })}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Novo
              </Button3D>
            )}
          </div>

          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">
              {catalogListTotalLabel(activeTab, recorteAtivo)}
            </p>
            <p className="truncate text-3xl font-bold leading-tight">{summary.count}</p>
            <p className="mt-0.5 text-xs text-white/70">
              {summary.count === 0
                ? `Nenhum ${activeTab === 'service' ? 'serviço' : 'produto'} neste filtro`
                : summary.variantCount > 0
                  ? plural(summary.variantCount, 'variação', 'variações')
                  : plural(
                      summary.count,
                      activeTab === 'service' ? 'serviço' : 'produto',
                      activeTab === 'service' ? 'serviços' : 'produtos',
                    )}
              {/* "no silent caps": o que precisa de atenção é DITO. */}
              {summary.lowStockCount > 0 &&
                ` · ${plural(summary.lowStockCount, 'com estoque baixo', 'com estoque baixo')}`}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* Tipo de item: a única troca de CONTEXTO da tela, e por isso a
              única que usa a pílula 3D azul do componente base (#25/#188). */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ItemType)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="service" className="gap-2">
                <Wrench className="h-4 w-4" />
                Serviços
              </TabsTrigger>
              <TabsTrigger value="product" className="gap-2">
                <Package className="h-4 w-4" />
                Produtos
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* BLOCO DE CONTROLES DA LISTA — busca, recorte e seleção: um grupo
              só, com `space-y-2` em vez do `space-y-5` das SEÇÕES (#191/#194). */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar nome, código ou categoria"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {showBarcodeButton && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-xl"
                  onClick={handleScanBarcode}
                  disabled={isBarcodeScanning}
                  aria-label="Escanear código de barras"
                  title="Escanear código de barras"
                >
                  {isBarcodeScanning ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ScanLine className="h-4 w-4" />
                  )}
                </Button>
              )}

              {/* Ordenação: menu, não modal — escolha única entre cinco rótulos
                  curtos. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl"
                    aria-label={`Ordenar (${CATALOG_SORT_LABELS[sortOption]})`}
                    title={`Ordenar: ${CATALOG_SORT_LABELS[sortOption]}`}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {CATALOG_SORT_ORDER.map((option) => (
                    <DropdownMenuItem
                      key={option}
                      onSelect={() => setSortOption(option)}
                      className="gap-2"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                      </span>
                      {CATALOG_SORT_LABELS[option]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Filtros: aqui SIM um modal — estoque e faixa de preço fazem
                  sentido escolhidos juntos, e o rascunho do diálogo é o que
                  impede a lista de se reorganizar a cada tecla do campo de
                  preço (#157). */}
              <Button
                variant="outline"
                size="icon"
                className="relative h-10 w-10 shrink-0 rounded-xl"
                onClick={() => setFiltersOpen(true)}
                aria-label={
                  activeModalFilters > 0
                    ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
                    : 'Filtros'
                }
                title="Filtros"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {activeModalFilters > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                    {activeModalFilters}
                  </span>
                )}
              </Button>
            </div>

            {/* CATEGORIA — o análogo do STATUS de `/quotes`: o recorte
                intrínseco desta lista. Ela era um `<Select>` escondendo o
                recorte mais usado atrás de dois cliques; como botões, ele fica
                à vista e cada um diz quantos itens traria (no `title`, onde não
                custa pixel — #191). Aqui os rótulos são TEXTO LIVRE do usuário
                e a quantidade é desconhecida, então o `overflow-x-auto` da
                faixa rola de verdade — é ele que impede o #185. */}
            {categories.length > 0 && (
              <div className={FILTER_CHIP_ROW}>
                {[{ value: 'all', label: 'Todas' }, ...categories.map((c) => ({ value: c, label: c }))].map(
                  (option) => {
                    const active = categoryFilter === option.value;
                    const count =
                      option.value === 'all'
                        ? groupsBeforeCategory.length
                        : (categoryCounts.get(option.value) ?? 0);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setCategoryFilter(option.value)}
                        aria-pressed={active}
                        aria-label={`${option.label} (${count})`}
                        title={`${option.label}: ${plural(count, 'item', 'itens')}`}
                        className={cn(
                          FILTER_CHIP_COMPACT,
                          active ? FILTER_CHIP_ON : FILTER_CHIP_OFF,
                        )}
                      >
                        {option.label}
                      </button>
                    );
                  },
                )}
              </div>
            )}

            {/* Entrada e saída do MODO DE SELEÇÃO (#192). */}
            {!loading && filteredItems.length > 0 && (
              <div className="flex items-center justify-between gap-2 px-1">
                {selectionMode ? (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={(checked) => toggleSelectAll(!!checked, filteredItems)}
                      aria-label={`Selecionar todos os ${activeTab === 'service' ? 'serviços' : 'produtos'}`}
                    />
                    <span
                      className="cursor-pointer text-sm text-muted-foreground"
                      onClick={() => toggleSelectAll(!allSelected, filteredItems)}
                    >
                      Selecionar todos ({filteredItems.length})
                    </span>
                  </div>
                ) : (
                  <span className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
                    <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{CATALOG_SORT_LABELS[sortOption]}</span>
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (selectionMode) clearSelection();
                    setSelectionMode((prev) => !prev);
                  }}
                  className="shrink-0 rounded-full px-2 py-1 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
                >
                  {selectionMode ? 'Concluir' : 'Selecionar'}
                </button>
              </div>
            )}

            <ItemList
              groups={visibleGroups}
              loading={loading}
              filtered={recorteAtivo}
              formatCurrency={formatCurrency}
              getUnitLabel={getUnitLabel}
              emptyText={activeTab === 'service' ? 'serviço' : 'produto'}
              onItemClick={(item) => navigate(`/catalog/${isVariant(item) ? item.parent_product_id : item.id}/edit`)}
              isSelected={isSelected}
              toggleItem={toggleItem}
              selectionMode={selectionMode}
              expandedParents={expandedParents}
              onToggleExpanded={toggleExpanded}
            />
          </div>
        </div>
      </div>

      {/* Bulk Action Bar — shown to members+ (they keep bulk duplicate);
          bulk delete button appears only for owner/admin. Viewers: hidden. */}
      {canWrite && (
        <BulkActionBar
          selectedCount={selectedCount}
          totalCount={filteredItems.length}
          allSelected={allSelected}
          onSelectAll={(checked) => toggleSelectAll(checked, filteredItems)}
          onDelete={canDelete ? () => setBulkDeleteOpen(true) : undefined}
          onClear={clearSelection}
          isDeleting={bulkDeleting}
          onDuplicate={handleBulkDuplicate}
          isDuplicating={bulkDuplicating}
          // Só quem escreve pode mexer no custo (gate de PAPEL, como o resto do
          // catálogo). Não há gate de PLANO: custo é campo de cadastro.
          extraAction={
            canWrite
              ? {
                  label: 'Custo',
                  icon: <Coins className="h-4 w-4" />,
                  onClick: handleOpenBulkCost,
                }
              : undefined
          }
        />
      )}

      <BulkCostModal
        open={bulkCostOpen}
        onOpenChange={setBulkCostOpen}
        selectedIds={Array.from(selectedIds)}
        products={items}
        onSuccess={fetchItems}
      />

      <BulkPriceAdjustModal
        open={bulkPriceAdjustOpen}
        onOpenChange={setBulkPriceAdjustOpen}
        categories={categories}
        products={items}
        onSuccess={fetchItems}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        count={selectedCount}
        itemType={selectedCount === 1 ? (activeTab === 'service' ? 'serviço' : 'produto') : (activeTab === 'service' ? 'serviços' : 'produtos')}
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleting}
      />

      {/* Filtros: estoque (só produtos) e faixa de preço.
          A CATEGORIA aparece só na faixa de botões: repeti-la aqui duplicaria
          um controle que a tela já mostra. */}
      <ListFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description={
          activeTab === 'product'
            ? 'Combine estoque e faixa de preço para achar um item.'
            : 'Recorte o catálogo por faixa de preço.'
        }
        groups={filterGroups}
        rangeLabel="Faixa de preço"
        value={{ groups: { [STOCK_GROUP_KEY]: stockFilter }, range: priceFilter }}
        onApply={(next) => {
          const wanted = next.groups[STOCK_GROUP_KEY] as CatalogStockFilter | undefined;
          setStockFilter(
            wanted && CATALOG_STOCK_FILTERS.some((o) => o.value === wanted) ? wanted : 'all',
          );
          setPriceFilter(next.range);
        }}
      />
    </MainLayout>
  );
}

function ItemList({
  groups,
  loading,
  filtered,
  formatCurrency,
  getUnitLabel,
  emptyText,
  onItemClick,
  isSelected,
  toggleItem,
  selectionMode,
  expandedParents,
  onToggleExpanded,
}: {
  /** Só RAÍZES, cada uma com suas variações anexadas. */
  groups: VariantGroup<CatalogItem>[];
  loading: boolean;
  /** Há recorte ativo? Decide entre "nenhum … encontrado" e "… cadastrado". */
  filtered: boolean;
  formatCurrency: (value: number) => string;
  getUnitLabel: (unit: string | null, type: ItemType) => string | null;
  emptyText: string;
  onItemClick: (item: CatalogItem) => void;
  isSelected: (id: string) => boolean;
  toggleItem: (id: string) => void;
  selectionMode: boolean;
  expandedParents: Set<string>;
  onToggleExpanded: (id: string) => void;
}) {
  const items = groups.map((g) => g.parent);
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className={cn(CONTENT_CARD, 'h-20 animate-pulse')} />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className={cn(
          CONTENT_CARD,
          'flex flex-col items-center justify-center border-dashed py-8 text-center',
        )}
      >
        {emptyText === 'serviço' ? (
          <Wrench className="mb-3 h-12 w-12 text-muted-foreground/50" />
        ) : (
          <Package className="mb-3 h-12 w-12 text-muted-foreground/50" />
        )}
        <p className="text-muted-foreground">
          {filtered ? `Nenhum ${emptyText} encontrado` : `Nenhum ${emptyText} cadastrado`}
        </p>
      </div>
    );
  }

  return (
    /* Duas colunas no desktop, como em `/quotes`: o card é uma linha densa
       (miniatura + nome à esquerda, preço e selos à direita) e esticá-lo pela
       largura toda mostraria menos itens por tela. `grid-cols-1` explícito no
       celular — sem ele a coluna implícita cresce até o min-content do nome
       mais longo e vaza a página (#185). */
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
      {groups.map(({ parent: item, variants, hasVariants }) => {
        const selected = isSelected(item.id);
        const expanded = expandedParents.has(String(item.id));
        const range = hasVariants ? priceRange(variants) : null;
        const variantsTrackStock = hasVariants && hasTrackedVariant(variants);
        return (
          <div
            key={item.id}
            className={cn(
              CONTENT_CARD,
              'cursor-pointer p-4 transition-shadow hover:shadow-md',
              selected && 'ring-2 ring-primary',
            )}
          >
            <div className="flex items-center gap-3">
              {/* A caixa só existe no MODO DE SELEÇÃO (#192): fora dele ela
                  ocupava espaço em toda linha por causa de uma ação que é
                  exceção — e neste card ela disputava com a MINIATURA. */}
              {selectionMode && (
                <Checkbox
                  checked={selected}
                  onCheckedChange={() => toggleItem(item.id)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Selecionar ${item.name}`}
                />
              )}
              {/* Thumbnail */}
              {item.image_url ? (
                <StorageImage
                  src={item.image_url}
                  alt={item.name}
                  className="h-10 w-10 shrink-0 rounded-md object-cover"
                  onError={(e) => {
                    // Hide broken image and show fallback
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`h-10 w-10 rounded-md bg-muted flex items-center justify-center shrink-0 ${item.image_url ? 'hidden' : ''}`}>
                <Image className="h-4 w-4 text-muted-foreground" />
              </div>
              <div
                className="flex min-w-0 flex-1 items-center justify-between gap-2"
                onClick={() => onItemClick(item)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-start gap-1.5">
                    {/* Offline pending indicator */}
                    {(item as any)._offline_pending && (
                      <span className="mt-0.5 inline-flex shrink-0 items-center text-amber-600" title="Aguardando sincronização">
                        <Clock className="h-3.5 w-3.5" />
                      </span>
                    )}
                    {/* D12: o nome dividia a linha com o preço e ficava com 113px
                        de 328 — dois produtos DIFERENTES saíam idênticos na tela
                        ("[E2E-DEV] Ca…"), e com dado real também ("Biscoito
                        Treloso …"). É a família do #52/#30d: o corte come
                        justamente o que distingue a linha. Ele ocupa a coluna
                        inteira e QUEBRA em quantas linhas precisar; o preço fica
                        na linha dos selos, que tinha sobra de espaço.

                        Sem `line-clamp`: com 2 linhas o nome de 65 caracteres
                        ainda perdia 29%, e "…Tamanho P" × "…Tamanho M" (o caso
                        do #30d, em que o sufixo é o que identifica) continuava
                        saindo idêntico. O card só cresce quando o nome é grande —
                        que é exatamente quando a informação importa.

                        O `min-w-0` NÃO é decorativo: um flex item nasce com
                        `min-width: auto`, e sem ele um nome sem espaço nenhum
                        empurra a linha para 657px e vaza a viewport (medido). O
                        `truncate` anterior escondia isso de graça, porque
                        `overflow: hidden` zera esse mínimo. */}
                    <p className="min-w-0 font-medium leading-snug break-words">{item.name}</p>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    {/* O pai não tem preço único: mostra a faixa das variações. */}
                    <span className="font-semibold text-primary">
                      {range
                        ? range.min === range.max
                          ? formatCurrency(range.min)
                          : `${formatCurrency(range.min)} – ${formatCurrency(range.max)}`
                        : formatCurrency(item.price)}
                    </span>
                    {item.description && (
                      <span className="truncate max-w-[120px]">{item.description}</span>
                    )}
                    {getUnitLabel(item.unit, item.type) && (
                      <Badge variant="outline" className="text-xs shrink-0">
                        {getUnitLabel(item.unit, item.type)}
                      </Badge>
                    )}
                    {/* Com variações o selo conta as filhas; sem elas, o saldo
                        do próprio produto (comportamento de sempre). */}
                    {hasVariants ? (
                      <Badge variant="outline" className="text-xs shrink-0 gap-1">
                        <Layers className="h-3 w-3" />
                        {variants.length} {variants.length === 1 ? 'variação' : 'variações'}
                      </Badge>
                    ) : (
                      isTrackedProduct(item) && (
                        <Badge
                          variant={isLowStock(item) ? 'destructive' : 'secondary'}
                          className="text-xs shrink-0 gap-1"
                          title={isLowStock(item) ? 'Estoque baixo' : 'Saldo em estoque'}
                        >
                          {isLowStock(item) ? <AlertTriangle className="h-3 w-3" /> : <Boxes className="h-3 w-3" />}
                          {formatStockQuantity(item.stock_quantity)}
                        </Badge>
                      )
                    )}
                    {/* Saldo do pai = soma das variações rastreadas (ele não
                        tem saldo próprio). */}
                    {variantsTrackStock && (
                      <Badge variant="secondary" className="text-xs shrink-0 gap-1" title="Saldo somado das variações">
                        <Boxes className="h-3 w-3" />
                        {formatStockQuantity(totalVariantStock(variants))}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {hasVariants && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpanded(String(item.id));
                  }}
                  aria-label={expanded ? `Ocultar variações de ${item.name}` : `Ver variações de ${item.name}`}
                  aria-expanded={expanded}
                >
                  {expanded ? <ChevronDown className="h-4 w-4" /> : <Layers className="h-4 w-4" />}
                </Button>
              )}
            </div>
            {/* Variações aninhadas: cada uma é a unidade que vende e estoca.
                Clicar abre o mesmo formulário do pai (nome/foto/categoria são
                dele) com a lista de variações carregada. */}
            {hasVariants && expanded && (
              <div className="mt-1 space-y-1 border-t pt-2 pl-11">
                {variants.map((variant) => (
                  <div
                    key={variant.id}
                    className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-muted/60"
                    onClick={(e) => {
                      e.stopPropagation();
                      onItemClick(variant);
                    }}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="truncate text-sm font-medium">{variant.variant_name}</span>
                      {variant.barcode && (
                        <span className="truncate font-mono text-[10px] text-muted-foreground">
                          {variant.barcode}
                        </span>
                      )}
                      {isTrackedProduct(variant) && (
                        <Badge
                          variant={isLowStock(variant) ? 'destructive' : 'secondary'}
                          className="shrink-0 gap-1 text-[10px]"
                          title={isLowStock(variant) ? 'Estoque baixo' : 'Saldo em estoque'}
                        >
                          {isLowStock(variant) ? (
                            <AlertTriangle className="h-3 w-3" />
                          ) : (
                            <Boxes className="h-3 w-3" />
                          )}
                          {formatStockQuantity(variant.stock_quantity)}
                        </Badge>
                      )}
                    </div>
                    <span className="shrink-0 text-sm font-semibold text-primary">
                      {formatCurrency(variant.price)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
